import { Fragment, useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bars3Icon,
  XMarkIcon,
  HomeIcon,
  UsersIcon,
  UserGroupIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  BanknotesIcon,
  DocumentChartBarIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MegaphoneIcon,
  ArrowPathIcon,
  ShieldExclamationIcon,
  CreditCardIcon,
  WalletIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { useAuthStore } from '../store/auth';
import { useBrandingStore } from '../store/branding';
import NotificationBell from './NotificationBell';
import clsx from 'clsx';

const navigation = [
  { name: 'Dashboard', href: '/', icon: HomeIcon },
  { name: 'Campaigns', href: '/campaigns', icon: MegaphoneIcon },
  { name: 'Affiliates', href: '/affiliates', icon: UsersIcon },
  { name: 'Players', href: '/players', icon: UserGroupIcon },
  { name: 'Traders', href: '/traders', icon: ChartBarIcon },
  { name: 'Conversions', href: '/conversions', icon: ArrowPathIcon },
  { name: 'Commissions', href: '/commissions', icon: CurrencyDollarIcon },
  { name: 'Payouts', href: '/payouts', icon: BanknotesIcon },
  { name: 'Payout Requests', href: '/payout-management', icon: WalletIcon },
  { name: 'Invoices', href: '/invoices', icon: DocumentTextIcon },
  { name: 'Payment Providers', href: '/payment-settings', icon: CreditCardIcon },
  { name: 'Fraud Rules', href: '/fraud', icon: ShieldExclamationIcon },
  { name: 'Reports', href: '/reports', icon: DocumentChartBarIcon },
  { name: 'Settings', href: '/settings', icon: Cog6ToothIcon },
];

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });
  const { user, logout } = useAuthStore();
  const { logoUrl, tenantName, fetchBranding, applyColors, applyFavicon, isLoaded } = useBrandingStore();
  const navigate = useNavigate();

  // Fetch branding on mount
  useEffect(() => {
    fetchBranding();
  }, [fetchBranding]);

  // Apply colors and favicon when loaded
  useEffect(() => {
    if (isLoaded) {
      applyColors();
      applyFavicon();
    }
  }, [isLoaded, applyColors, applyFavicon]);

  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(collapsed));
  }, [collapsed]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-[var(--bg-primary)]">
      {/* Mobile sidebar */}
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50 lg:hidden" onClose={setSidebarOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1 flex-col">
                <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                  <button type="button" className="-m-2.5 p-2.5" onClick={() => setSidebarOpen(false)}>
                    <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
                  </button>
                </div>

                {/* Gradient accent bar at top */}
                <div className="h-1 bg-gradient-to-r from-primary-500 to-accent-500 w-full shrink-0"></div>
                <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-[var(--bg-secondary)] px-6 pb-4 border-r border-[var(--border-color)]">
                  <div className="flex h-16 shrink-0 items-center">
                    {logoUrl ? (
                      <img src={logoUrl} alt={tenantName || 'Logo'} className="h-12 max-w-[180px] object-contain" />
                    ) : (
                      <span className="text-gradient text-xl font-bold font-display">{tenantName || 'Dashboard'}</span>
                    )}
                  </div>
                  <nav className="flex flex-1 flex-col">
                    <ul role="list" className="flex flex-1 flex-col gap-y-7">
                      <li>
                        <ul role="list" className="-mx-2 space-y-1">
                          {navigation.map((item) => (
                            <li key={item.name}>
                              <NavLink
                                to={item.href}
                                onClick={() => setSidebarOpen(false)}
                                className={({ isActive }) =>
                                  clsx(
                                    isActive
                                      ? 'bg-gradient-to-r from-primary-600 to-accent-500 text-white shadow-glow'
                                      : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-card)]',
                                    'group flex gap-x-3 rounded-xl p-3 text-sm leading-6 font-semibold transition-all duration-200'
                                  )
                                }
                              >
                                <item.icon className="h-6 w-6 shrink-0" aria-hidden="true" />
                                {item.name}
                              </NavLink>
                            </li>
                          ))}
                        </ul>
                      </li>
                    </ul>
                  </nav>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Desktop sidebar */}
      <div
        className={clsx(
          'hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:flex-col transition-all duration-300',
          collapsed ? 'lg:w-20' : 'lg:w-72'
        )}
      >
        {/* Gradient accent bar at top */}
        <div className="h-1 bg-gradient-to-r from-primary-500 to-accent-500 w-full shrink-0"></div>
        <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-[var(--bg-secondary)] px-3 pb-4 border-r border-[var(--border-color)]">
          {/* Logo / Title */}
          <div className="flex h-16 shrink-0 items-center justify-between">
            <AnimatePresence mode="wait">
              {!collapsed && (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  {logoUrl ? (
                    <img src={logoUrl} alt={tenantName || 'Logo'} className="h-12 max-w-[180px] object-contain px-2" />
                  ) : (
                    <span className="text-gradient text-xl font-bold px-2 font-display">{tenantName || 'Dashboard'}</span>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
            <button
              onClick={() => setCollapsed(!collapsed)}
              className={clsx(
                'p-2 rounded-xl text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-card)] transition-all duration-200',
                collapsed && 'mx-auto'
              )}
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? (
                <ChevronRightIcon className="h-5 w-5" />
              ) : (
                <ChevronLeftIcon className="h-5 w-5" />
              )}
            </button>
          </div>

          <nav className="flex flex-1 flex-col">
            <ul role="list" className="flex flex-1 flex-col gap-y-7">
              <li>
                <ul role="list" className="space-y-1">
                  {navigation.map((item, index) => (
                    <motion.li
                      key={item.name}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.03 }}
                    >
                      <NavLink
                        to={item.href}
                        className={({ isActive }) =>
                          clsx(
                            isActive
                              ? 'bg-gradient-to-r from-primary-600 to-accent-500 text-white shadow-glow'
                              : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-card)]',
                            'group flex rounded-xl p-3 text-sm leading-6 font-semibold transition-all duration-200',
                            collapsed ? 'justify-center' : 'gap-x-3 px-3'
                          )
                        }
                        title={collapsed ? item.name : undefined}
                      >
                        <item.icon className="h-6 w-6 shrink-0" aria-hidden="true" />
                        <AnimatePresence mode="wait">
                          {!collapsed && (
                            <motion.span
                              initial={{ opacity: 0, width: 0 }}
                              animate={{ opacity: 1, width: 'auto' }}
                              exit={{ opacity: 0, width: 0 }}
                              transition={{ duration: 0.2 }}
                            >
                              {item.name}
                            </motion.span>
                          )}
                        </AnimatePresence>
                      </NavLink>
                    </motion.li>
                  ))}
                </ul>
              </li>

              {/* Bottom section - User profile */}
              <li className="mt-auto border-t border-[var(--border-color)] pt-3">
                <NavLink
                  to="/profile"
                  className={({ isActive }) =>
                    clsx(
                      isActive
                        ? 'bg-[var(--bg-card)]'
                        : 'hover:bg-[var(--bg-card)]',
                      'flex items-center rounded-xl p-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-all duration-200',
                      collapsed ? 'flex-col gap-2 justify-center' : 'gap-x-3 px-3'
                    )
                  }
                  title={collapsed ? user?.name : undefined}
                >
                  {user?.avatarUrl ? (
                    <img
                      src={user.avatarUrl}
                      alt={user.name}
                      className="h-8 w-8 rounded-full object-cover shrink-0 ring-2 ring-primary-500/30"
                    />
                  ) : (
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary-500 to-accent-500 text-white text-sm font-semibold">
                      {user?.name?.charAt(0).toUpperCase()}
                    </span>
                  )}
                  <AnimatePresence mode="wait">
                    {!collapsed && (
                      <motion.span
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="truncate flex-1 text-sm font-semibold"
                      >
                        {user?.name}
                      </motion.span>
                    )}
                  </AnimatePresence>
                </NavLink>

                <button
                  onClick={handleLogout}
                  className={clsx(
                    'flex items-center rounded-xl p-2 text-[var(--text-muted)] hover:text-error-400 hover:bg-error-500/10 transition-all duration-200 mt-1 w-full',
                    collapsed ? 'justify-center' : 'gap-x-3 px-3'
                  )}
                  title="Logout"
                >
                  <ArrowRightOnRectangleIcon className="h-5 w-5" />
                  <AnimatePresence mode="wait">
                    {!collapsed && (
                      <motion.span
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="text-sm font-semibold"
                      >
                        Logout
                      </motion.span>
                    )}
                  </AnimatePresence>
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div
        className={clsx(
          'transition-all duration-300',
          collapsed ? 'lg:pl-20' : 'lg:pl-72'
        )}
      >
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-[var(--border-color)] bg-[var(--bg-secondary)]/80 backdrop-blur-xl px-4 shadow-lg shadow-black/5 sm:gap-x-6 sm:px-6 lg:px-8">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-[var(--text-muted)] hover:text-[var(--text-primary)] lg:hidden transition-colors"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
          </button>

          {/* Separator for mobile */}
          <div className="h-6 w-px bg-[var(--border-color)] lg:hidden" />

          {/* Right side of header - notifications, search, etc. */}
          <div className="flex flex-1 justify-end gap-x-4 lg:gap-x-6">
            <NotificationBell />
          </div>
        </div>

        <main className="py-10">
          <div className="px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Outlet />
            </motion.div>
          </div>
        </main>
      </div>
    </div>
  );
}
